import { Grid } from "@mui/material"
import { push } from "connected-react-router"
import { createContext, FC, useCallback, useContext } from "react"
import { useDispatch } from "react-redux"
import { Content } from "@/components/content/Content"
import { useDatabaseSelectCard, useObfuscatableDatabasesSource } from "@/components/databases/DatabaseSelectCard"
import HistoricalObfuscationTable from "@/components/obfuscation/HistoricalObfuscationTable"
import { DatabaseResponseDto } from "@/gen/api/backup"
import { OdResourcePageProps } from "@/types/resource"
import { crudGetList } from "ra-core"
import { pages, sorts } from "@/utils/paramsWithGetMainList"
import { useResourceRegister } from "@/utils/resourceRegister"
import { decodeHashid, genUrlWithHashid } from "@/utils/hashid"
import { EncryptedDatabasesContextProvider } from "@/components/databases/useEncryptedDatabases"


// 
// controller/context/provider
// 

export interface ObfuscationDetailControllerProps {
  fireConfigFileDownload(dbId: number, encryptionId: number): void;
}

const ObfuscationDetailContext = createContext<ObfuscationDetailControllerProps>({
  fireConfigFileDownload: () => Promise.reject(),
})


export const ObfuscationDetailContextProvider: FC<{ value: ObfuscationDetailControllerProps }> = ({ children, value }) => {
  return (
    <ObfuscationDetailContext.Provider value={value}>
      {children}
    </ObfuscationDetailContext.Provider>
  )
}

export const useObfuscationDetailController = (): ObfuscationDetailControllerProps => {
  const dispatch = useDispatch()

  const fireConfigFileDownload = useCallback((dbId: number, encryptionId: number) => {
    dispatch(crudGetList('encryption-rec-file', pages(), sorts(), { dbId, encryptionId }))
  }, [dispatch])

  return {
    fireConfigFileDownload,
  }
}

export const useObfuscationDetailContext = () => useContext(ObfuscationDetailContext);


// 
// view components: main/cater/body
// 


const ObfuscationDetail: FC<OdResourcePageProps & { id?: string}> = (props) => {
  useResourceRegister({ name: 'obfuscation-historic-recs' })
  useResourceRegister({ name: 'encryption-rec-file' })
  const { id: hId, ...rest } = props
  const id = decodeHashid(hId || '') 
  const dispatch = useDispatch()

  const handleSwitchDatabase = useCallback((db: DatabaseResponseDto) => {
    dispatch(push(
      genUrlWithHashid(`/encryption/obfs/list/$0/show`, db.databaseId || 0, db.name)))
  }, [dispatch])

  const pageCtrls = useObfuscationDetailController()
  return (
    <ObfuscationDetailContextProvider value={pageCtrls}>
      <EncryptedDatabasesContextProvider>
        <ObfuscationDetailBody onSwitchDatabase={handleSwitchDatabase} {...{id}} {...rest} />
      </EncryptedDatabasesContextProvider>
    </ObfuscationDetailContextProvider>
  )
}

export interface ObfuscationDetailBodyProps extends OdResourcePageProps {
  onSwitchDatabase: (x: DatabaseResponseDto) => void;
  id: number;
}

export const ObfuscationDetailBody: FC<ObfuscationDetailBodyProps> = ({
  id,
  onSwitchDatabase,
  options,
  resource,
}) => {
  const DatabaseSelectCard = useDatabaseSelectCard(useObfuscatableDatabasesSource)
  
  const handleChange = useCallback((e: any, chosenDb?: DatabaseResponseDto) => {
    if (chosenDb?.databaseId) {
      onSwitchDatabase(chosenDb)
    }
  }, [onSwitchDatabase])

  return (
    <Content 
      pageName="Obfuscation Detail" 
      resourceName="Detail" 
      breadcrumbs={[...(options?.breadcrumbs || []), { label: 'Obfuscation Jobs', path: '/encryption/obfs/list' }]}
    >
      <Grid container spacing={4}>
        <Grid
          item
          md={6}
          xs={12}
        >
          <DatabaseSelectCard preSelectDbId={id} onChange={handleChange} />
        </Grid>
        <Grid
          item
          xs={12}
        >
          <HistoricalObfuscationTable databaseId={id} />
        </Grid>
      </Grid>
    </Content>
  )
}



export default ObfuscationDetail;
